import { z } from "zod";

import { sendGoogleAnalyticsEvent } from "./google-tag";

const categorySchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9_:-]+$/u);
const entityTypeSchema = z.enum(["release", "work"]);
const channelSchema = z.enum(["email", "telegram", "whatsapp", "web_push"]);
const methodSchema = z.enum(["google", "telegram", "phone", "email"]);

const commonEventPropertiesSchema = z.strictObject({
  entity_type: categorySchema.optional(),
  surface: categorySchema.optional(),
});

function eventProperties<const Shape extends z.ZodRawShape>(shape: Shape) {
  return commonEventPropertiesSchema.extend(shape);
}

export const clientAnalyticsEventSchema = z.discriminatedUnion("name", [
  z.strictObject({
    name: z.literal("authentication_started"),
    properties: eventProperties({
      method: methodSchema,
      intent: z.enum(["sign_up", "login"]),
      surface: categorySchema,
    }),
  }),
  z.strictObject({
    name: z.literal("sign_up"),
    properties: eventProperties({ method: methodSchema }),
  }),
  z.strictObject({
    name: z.literal("login"),
    properties: eventProperties({ method: methodSchema }),
  }),
  z.strictObject({
    name: z.literal("offer_clicked"),
    properties: z.strictObject({}),
  }),
  z.strictObject({
    name: z.literal("onboarding_step_viewed"),
    properties: eventProperties({ step: z.number().int().min(1).max(3) }),
  }),
  z.strictObject({
    name: z.literal("catalog_search_submitted"),
    properties: eventProperties({ surface: categorySchema }),
  }),
  z.strictObject({
    name: z.literal("catalog_results_viewed"),
    properties: eventProperties({
      surface: categorySchema,
      result_state: z.enum(["empty", "available"]),
      result_count_bucket: z.enum(["0", "1_10", "11_50", "51_100", "101_plus"]),
    }),
  }),
  z.strictObject({
    name: z.literal("catalog_item_viewed"),
    properties: eventProperties({ entity_type: entityTypeSchema, surface: categorySchema }),
  }),
  z.strictObject({
    name: z.literal("onboarding_completed"),
    properties: eventProperties({ completion_path: categorySchema }),
  }),
  z.strictObject({
    name: z.literal("post_onboarding_viewed"),
    properties: eventProperties({
      history_state: z.enum(["available", "empty"]),
      record_count_bucket: z.enum(["0", "1_3", "4_6"]),
    }),
  }),
  z.strictObject({
    name: z.literal("taste_preferences_updated"),
    properties: eventProperties({
      format_count: z.number().int().min(0).max(20),
      style_count: z.number().int().min(0).max(100),
    }),
  }),
  z.strictObject({
    name: z.literal("collection_item_added"),
    properties: eventProperties({
      entity_type: entityTypeSchema,
      item_count: z.number().int().min(1).max(500),
      surface: categorySchema,
    }),
  }),
  z.strictObject({
    name: z.literal("wishlist_add_started"),
    properties: eventProperties({ entity_type: entityTypeSchema, surface: categorySchema }),
  }),
  z.strictObject({
    name: z.literal("wishlist_add_failed"),
    properties: eventProperties({
      entity_type: entityTypeSchema,
      failure_reason: z.enum([
        "authentication",
        "validation",
        "conflict",
        "network",
        "not_found",
        "unknown",
      ]),
      surface: categorySchema,
    }),
  }),
  z.strictObject({
    name: z.literal("wishlist_item_added"),
    properties: eventProperties({
      entity_type: entityTypeSchema,
      item_count: z.number().int().min(1).max(500),
      surface: categorySchema,
    }),
  }),
  z.strictObject({
    name: z.literal("notification_prompt_viewed"),
    properties: eventProperties({ surface: categorySchema }),
  }),
  z.strictObject({
    name: z.literal("notification_prompt_dismissed"),
    properties: eventProperties({ surface: categorySchema }),
  }),
  z.strictObject({
    name: z.literal("notification_setup_started"),
    properties: eventProperties({ channel: channelSchema, surface: categorySchema }),
  }),
  z.strictObject({
    name: z.literal("notification_setup_failed"),
    properties: eventProperties({
      channel: channelSchema,
      failure_reason: categorySchema,
      surface: categorySchema,
    }),
  }),
  z.strictObject({
    name: z.literal("notification_channel_enabled"),
    properties: eventProperties({ channel: channelSchema, surface: categorySchema }),
  }),
  z.strictObject({
    name: z.literal("public_channel_join_clicked"),
    properties: eventProperties({
      channel: z.literal("whatsapp"),
      destination: z.enum(["vinyl", "cd", "community"]),
      surface: categorySchema,
    }),
  }),
]);

export type ClientAnalyticsEvent = z.input<typeof clientAnalyticsEventSchema>;

/** The only browser product-event transport. */
export function trackClientEvent(rawEvent: ClientAnalyticsEvent): void {
  const event = clientAnalyticsEventSchema.safeParse(rawEvent).data;
  if (!event) return;
  sendGoogleAnalyticsEvent(event.name, event.properties);
}
